import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectProps, useTheme, alpha } from "@mui/material";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../configureStore";
import { TextStyleProps } from "../types/formConfig";

interface SelectOption {
    value: string;
    label: string;
}

interface FormSelectProps extends Omit<SelectProps, 'value' | 'onChange'> {
    fieldPath: string;
    value: any;
    options: SelectOption[];
    handleFieldChange: (fieldPath: string, value: any) => void;
    isImportant?: boolean;
    width?: number | string;
    labelStyle?: TextStyleProps;
    inputStyle?: TextStyleProps;
    multiple?: boolean;
}

const FormSelect = ({
    fieldPath,
    value,
    options,
    handleFieldChange,
    isImportant = false,
    width,
    label,
    labelStyle,
    inputStyle,
    multiple = false,
    ...selectProps
}: FormSelectProps) => {
    const theme = useTheme();
    const showOnlyImportant = useSelector<RootState, boolean>(({ ui }) => ui.showOnlyImportantFields);
    const importantBorderColor = theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00';

    const handleChange = useCallback((event: any) => {
        handleFieldChange(fieldPath, event.target.value);
    }, [fieldPath, handleFieldChange]);

    // If showOnlyImportant is true and this field is not important, don't render it
    if (showOnlyImportant && !isImportant) {
        return null;
    }

    const formControlStyles = {
        width: width || 'auto',
        '& .MuiInputBase-root': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.8)
                : theme.palette.background.paper,
        },
        '& .MuiInputLabel-root': {
            color: theme.palette.text.secondary,
            ...(isImportant && {
                color: importantBorderColor,
                fontWeight: 600,
            }),
            ...labelStyle,
        },
        '& .MuiSelect-select': {
            ...inputStyle,
            '& .MuiListItemText-primary': {
                ...inputStyle,
            },
            '& .MuiTypography-root': {
                ...inputStyle,
            },
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: isImportant ? importantBorderColor : theme.palette.divider,
                borderWidth: isImportant ? 2 : 1,
            },
            '&:hover fieldset': {
                borderColor: isImportant ? importantBorderColor : theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: isImportant ? importantBorderColor : theme.palette.primary.main,
                boxShadow: isImportant ? `0 0 0 3px ${alpha(importantBorderColor, 0.2)}` : 'none',
            },
        },
    };

    const selectedValues: string[] = Array.isArray(value) ? value : [];
    const renderMultiValue = (selected: unknown) => {
        const vals = Array.isArray(selected) ? (selected as string[]) : [];
        return options
            .filter((o) => vals.includes(o.value))
            .map((o) => o.label)
            .join(', ');
    };

    return (
        <FormControl size="small" sx={formControlStyles}>
            {label && <InputLabel>{label}</InputLabel>}
            <Select
                multiple={multiple}
                value={multiple ? selectedValues : value || ''}
                onChange={handleChange}
                label={label}
                renderValue={multiple ? renderMultiValue : undefined}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            bgcolor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#fff',
                            border: 1,
                            borderColor: theme.palette.divider,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 4px 20px rgba(0,0,0,0.5)'
                                : '0 4px 20px rgba(0,0,0,0.15)',
                            '& .MuiMenuItem-root': {
                                ...labelStyle,
                                '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.main, 0.15)
                                        : alpha(theme.palette.primary.main, 0.08),
                                },
                                '&.Mui-selected': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                            },
                            '& .MuiListItemText-primary': {
                                ...labelStyle,
                            },
                        },
                    },
                }}
                {...selectProps}
            >
                {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {multiple && (
                            <Checkbox
                                checked={selectedValues.includes(option.value)}
                                size="small"
                                sx={{ p: 0.5 }}
                            />
                        )}
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default FormSelect;
